import PropTypes from 'prop-types';
import React from 'react';
import debounce from 'lodash/debounce';
import clamp from 'lodash/clamp';

import { observable, action, computed, autorun } from 'mobx';
import { observer } from 'mobx-react';

import Styled from 'styled';

import defaultStyles from './collection-view.scss';

@observer
class CollectionItemView extends React.Component {
    ref = React.createRef();

    @computed.struct get layout() {
        const { cols } = this.props.collectionView;

        const { height, width } = this.props;

        const { order } = this;

        const row = Math.floor(order / cols);
        const col = order % cols;

        return [(width || 0) * col, height * row];
    }

    @computed get order() {
        return this.props.collectionView.map.get(this.props.itemKey);
    }

    @computed get transform(): string {
        if (this.dragging) {
            const { x, y } = this.props.collectionView.dragCoordinates;
            return `translateX(${x}px) translateY(${y}px)`;
        }

        const { layout } = this;
        const [x, y] = layout;

        return `translateX(${x}px) translateY(${y}px)`;
    }

    @computed get zIndex() {
        const { size } = this.props.collectionView;

        return this.dragging ? size : this.props.order;
    }

    @computed get dragging() {
        return this.props.draggable && this.props.item === this.props.collectionView.dragItem;
    }

    onMouseDown = event => {
        if (this.props.draggable) {
            const [x, y] = this.layout;

            this.props.collectionView.mouseDrag(event, this.props.item, x, y);
        }
    };

    onTouchStart = event => {
        if (this.props.draggable) {
            const [x, y] = this.layout;

            this.props.collectionView.touchDrag(event, this.props.item, x, y);
        }
    };

    render() {
        const { draggable, children, styles, width, height } = this.props;

        const { zIndex, dragging, transform } = this;

        let grabClassName = '';

        if (draggable) {
            if (dragging) {
                grabClassName = styles.grabbing;
            } else {
                grabClassName = styles.grabbable;
            }
        }

        return (
            <li
                ref={this.ref}
                className={`${styles.item} ${grabClassName}`}
                onMouseDown={this.onMouseDown}
                onTouchStart={this.onTouchStart}
                style={{
                    width: width || '100%',
                    height,
                    transform,
                    zIndex,
                }}
            >
                {children}
            </li>
        );
    }
}

@observer
class CollectionView<T> extends React.Component {
    static contextTypes = {
        scrollView: PropTypes.object,
    };

    static defaultProps = {
        styles: defaultStyles,
        items: [],
        height: 120,
        scrollDuration: 350,
        scrollAmount: 25,
        scrollMargin: 40,
        center: true,
        maxContainerWidth: Number.POSITIVE_INFINITY,
    };

    disposer: ?Function;

    animating = false;
    @observable container: ?HTMLElement;
    @observable containerWidth = null;
    @observable map = observable.map();

    keyMap = new Map();
    itemMap = new Map();

    // drag and drop properties

    @observable mouseX = 0;
    @observable mouseY = 0;
    @observable dragX = 0;
    @observable dragY = 0;
    @observable.ref dragItem: ?any;

    @action setContainer = (ref: ?HTMLElement) => {
        requestAnimationFrame(() => {
            this.container = ref;
        });
    };

    @computed get size() {
        return this.props.items.length;
    }

    @computed.struct get dragCoordinates(): ?{ x: number, y: number } {
        if (!this.dragItem) {
            return null;
        }

        return {
            x: this.mouseX - this.dragX,
            y: this.mouseY - this.dragY,
        };
    }

    // layout properties

    @computed get cols(): number {
        const { width } = this.props;

        const { size } = this;

        if (width == null) {
            return 1;
        }

        let cols = 1;

        if (this.containerWidth != null && width !== 0) {
            cols = Math.floor(this.containerWidth / width);
        }

        if (cols > size) {
            cols = size;
        }

        return cols;
    }

    processItems = () => {
        const { itemKey, items } = this.props;

        this.itemMap.clear();

        const set = new Set(this.map.keys());
        const keySet = new Set(this.keyMap.keys());

        items.forEach((item, i) => {
            let key = this.keyMap.get(item);
            if (!key) {
                key = `${itemKey(item)}`;
                this.keyMap.set(item, key);
            }

            this.itemMap.set(key, item);
            this.map.set(key, i);

            keySet.delete(item);
            set.delete(key);
        });

        keySet.forEach(key => {
            this.keyMap.delete(key);
        });

        set.forEach(key => {
            this.map.delete(key);
        });
    };

    @action onResize = debounce(() => {
        const { container } = this;

        if (container) {
            this.containerWidth = container.clientWidth;
        }
    }, 200);

    mouseDrag = (event: MouseEvent, item: T, x: number, y: number) => {
        if (this.props.shouldDrag && !this.props.shouldDrag(event)) {
            return;
        }

        event.preventDefault();
        this.drag(event, item, x, y);
    };

    touchDrag = (event: TouchEvent, item: T, x: number, y: number) => {
        if (this.props.shouldDrag && !this.props.shouldDrag(event)) {
            return;
        }

        if (item && !this.prevented) {
            this.prevented = true;
            window.document.body.classList.add(this.props.styles.bodyScrollDisable);
        }

        if (event.touches.length) {
            event.preventDefault();
            this.drag(event.touches[0], item, x, y);
        }
    };

    @action drag = (event: { pageY: number, pageX: number }, item: T, x: number, y: number) => {
        const { pageY, pageX } = event;

        this.dragItem = item;

        this.mouseX = pageX;
        this.mouseY = pageY;

        this.dragX = pageX - x;
        this.dragY = pageY - y;
    };

    @action drop = () => {
        this.dragItem = null;
    };

    scrollToDidEnd = () => {
        this.animating = false;
        this.move();
    };

    move = () => {
        const { height, width, scrollDuration, scrollAmount, scrollMargin } = this.props;

        const { dragItem, size, cols, dragCoordinates } = this;

        if (dragCoordinates) {
            const rows = Math.floor(size / cols);
            const col = width == null ? 0 : clamp(Math.floor(dragCoordinates.x / width), 0, cols - 1);
            const row = clamp(Math.floor(dragCoordinates.y / height), 0, rows);
            const to = row * cols + col;

            const from = this.props.items.indexOf(dragItem);

            if (from > -1 && from !== to && from < size && to < size) {
                this.props.move(from, to);
            }

            const { scrollView } = this.context;
            const ref = scrollView && scrollView.ref;
            const pageYScroll = window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop;
            const screenHeight = window.screen.height;

            if (ref) {
                const currentScrollTop = ref.scrollTop;

                if (this.mouseY - pageYScroll + scrollMargin > screenHeight) {
                    if (!this.animating) {
                        this.animating = true;

                        if (window.requestAnimationFrame) {
                            requestAnimationFrame(() => {
                                if (ref.scrollTop !== currentScrollTop + scrollAmount) {
                                    ref.scrollTop = currentScrollTop + scrollAmount;
                                }
                            });
                        }

                        this.animating = false;
                    }
                } else if (this.mouseY - pageYScroll - scrollMargin < 0) {
                    if (!this.animating) {
                        this.animating = true;

                        if (window.requestAnimationFrame) {
                            requestAnimationFrame(() => {
                                if (ref.scrollTop !== currentScrollTop - scrollAmount) {
                                    ref.scrollTop = currentScrollTop - scrollAmount;
                                }
                            });
                        }

                        this.animating = false;
                    }
                }
            }
        }
    };

    onMouseUp = this.drop;

    onTouchEnd = event => {
        if (typeof window !== 'undefined' && this.prevented) {
            this.prevented = false;
            window.document.body.classList.remove(this.props.styles.bodyScrollDisable);
        }
        this.drop(event);
    };

    @action onMouseMove = (event: MouseEvent | Touch) => {
        const { pageX, pageY } = event;

        this.mouseX = pageX;
        this.mouseY = pageY;
        this.move();
    };

    @action onTouchMove = (event: TouchEvent) => {
        // iOS Fix (if element is text, ios will try to magnify it)
        if ((event.target.getAttribute('class') || '').split(' ').indexOf(this.props.styles.item) !== -1) {
            event.preventDefault();
        }

        if (event.touches.length) {
            this.onMouseMove(event.touches[0]);
        }
    };

    UNSAFE_componentWillMount() {
        this.processItems();
    }

    componentDidMount() {
        requestAnimationFrame(() => {
            const disposers = [
                autorun(() => {
                    if (this.container) {
                        const { maxContainerWidth } = this.props;
                        this.containerWidth = Math.min(this.container.clientWidth, maxContainerWidth);
                    }
                }),
                autorun(this.processItems),
            ];

            this.disposer = () =>
                disposers.forEach(disposer => {
                    disposer();
                });

            window.addEventListener('touchmove', this.onTouchMove);
            window.addEventListener('touchend', this.onTouchEnd);
            window.addEventListener('mousemove', this.onMouseMove);
            window.addEventListener('mouseup', this.onMouseUp);
            window.addEventListener('resize', this.onResize);
        });
    }

    componentWillUnmount() {
        if (this.disposer) {
            this.disposer();
        }

        window.removeEventListener('touchmove', this.onTouchMove);
        window.removeEventListener('touchend', this.onTouchEnd);
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
        window.removeEventListener('resize', this.onResize);
    }

    render() {
        const { center, width, height, element, isDraggable, styles } = this.props;

        const { size, cols } = this;

        const collectionHeight = (size && Math.ceil(size / cols) * height) || 0;

        const children = [];
        this.map.forEach((_, key) => {
            const item = this.itemMap.get(key);

            let draggable = true;

            if (isDraggable) {
                draggable = isDraggable(item);
            }

            children.push(
                <CollectionItemView
                    key={key}
                    styles={styles}
                    collectionView={this}
                    draggable={draggable}
                    item={item}
                    itemKey={key}
                    width={width}
                    height={height}
                >
                    {element(item)}
                </CollectionItemView>
            );
        });

        return (
            <div className={styles.wrapper} ref={this.setContainer}>
                <ul
                    className={`${styles.collection} ${center ? styles.centered : ''} ${
                        !this.container ? styles.initial : ''
                    }`}
                    style={{
                        height: collectionHeight,
                        width: width == null ? '100%' : width * cols,
                    }}
                >
                    {children}
                </ul>
            </div>
        );
    }
}

export default Styled(defaultStyles)(CollectionView);
